import {DashboardMenubar} from "../../components/menubar/menubar";
import {useSetPageTitle} from "../../hooks/useSetPageTitle";
import {BackButton} from "../../components/backbutton/backbutton";
import {useNavigate} from "react-router-dom";
import GenerateSurveyForm from "./generate-survey-form";
import {GenerateSurveyFormValues} from "./generate-survey.types";
import useGenerateSurvey from "../../hooks/useGenerateSurvey";
import useCreateNewSurvey from "../../hooks/useCreateNewSurvey";
import ValidationStepsModal from "../../components/moderation-dialog/moderation-dialog";
import {
  GENERATION_INTERVAL,
  GENERATION_LOADING_STEPS,
  GENERATION_LOADING_TITLE,
  RETRYING_GENERATION_TITLE,
} from "./generate-survey.constants";
import { useMemo, useEffect, useState } from "react";
import { useGetTemplatesQuery } from "src/redux/survey/template-endpoints";
import { SurveyTemplate } from "src/redux/survey/template.types";
import { AppLayout } from "../../components/layout/app-layout";
import { shouldUseNewLayout } from "../../helpers/layout.helpers";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/redux-store";

const GenerateSurveyScreen = () => {
  useSetPageTitle('Generate Survey');
  const navigate = useNavigate();
  const { generateSurvey, isGenerating, isRetrying } = useGenerateSurvey();
  const { createNewSurvey } = useCreateNewSurvey();
  const userState = useSelector((state: RootState) => state.userState);
  const useNewLayout = shouldUseNewLayout(userState.permissions);

  const surveyTabs = [
    {
      id: 'generate',
      label: 'Generate Survey',
      onClick: () => navigate('/generate-survey'),
    },
    {
      id: 'templates',
      label: 'Templates',
      onClick: () => navigate('/templates'),
    },
    {
      id: 'build',
      label: 'Build',
      onClick: () => createNewSurvey(),
    },
  ];

  const {validationSteps, validationTitle} = useMemo(() => {
    const baseSteps = GENERATION_LOADING_STEPS;

    if (isRetrying) {
      baseSteps.unshift('Taking it from the top');
    }

    return { validationSteps: baseSteps, validationTitle: isRetrying ? RETRYING_GENERATION_TITLE : GENERATION_LOADING_TITLE };
  }, [isGenerating, isRetrying]);

  const urlParams = new URLSearchParams(window.location.search);
  const templateId = urlParams.get('template');

  const onBackClick = () => {
    navigate(templateId ? '/templates' : '/dashboard');
  };

  const onFormSubmission = (template_id: string, values: GenerateSurveyFormValues) => {
    void generateSurvey(template_id, values);
  };

  const { data: templatesData } = useGetTemplatesQuery(null);
  const [template, setTemplate] = useState<SurveyTemplate | null>(null);

  useEffect(() => {
    if (templatesData) {
      const allTemplates: SurveyTemplate[] = Object.values(templatesData).flat() as SurveyTemplate[];
      const foundTemplate = allTemplates.find(template => template.id === templateId);
      if (foundTemplate) {
        setTemplate(foundTemplate);
      } else {
        const defaultTemplate = allTemplates.find(template => template.category === "DEFAULT");
        setTemplate(defaultTemplate || null);
      }
    }
  }, [templatesData, templateId]);

  // Legacy layout for client users
  if (!useNewLayout) {
    return (
      <div>
        <DashboardMenubar />
        <div className="flex-column flex justify-center p-0 md:p-6">
          <div className="flex flex-row sm-max:flex-col items-start gap-4">
            <div className="ml-2 md:ml-0">
              <BackButton
                theme="light"
                onClick={onBackClick}
              />
            </div>
            <div
              className="flex flex-col items-center justify-start py-8 pl-4 md:pl-8 pr-10 md:pr-20 rounded-t-2xl md:rounded-2xl bg-white md:shadow-md"
            >
              <div className="text-3xl font-light">
                Generate a survey
              </div>
              <GenerateSurveyForm
                isLoading={false}
                onSubmit={onFormSubmission}
                template={template}
              />
            </div>
          </div>
        </div>
        <ValidationStepsModal
          isModalOpen={isGenerating}
          handleCloseModal={() => {}}
          showFooter={false}
          steps={validationSteps}
          title={validationTitle}
          updateInterval={GENERATION_INTERVAL}
          windowSize={6}
        />
      </div>
    );
  }

  // New layout for org admins and regular users
  return (
    <>
      <AppLayout
        activePage="/generate-survey"
        navbarProps={{
          showBackButton: true,
          backPath: templateId ? '/templates' : '/dashboard',
          tabs: surveyTabs,
          activeTab: 'generate',
        }}
      >
        <div className="flex justify-center">
          <div className="w-full max-w-4xl">
            <div className="bg-white rounded-lg shadow-md p-8">
              <GenerateSurveyForm
                isLoading={false}
                onSubmit={onFormSubmission}
                template={template}
              />
            </div>
          </div>
        </div>
      </AppLayout>
      <ValidationStepsModal
        isModalOpen={isGenerating}
        handleCloseModal={() => {}}
        showFooter={false}
        steps={validationSteps}
        title={validationTitle}
        updateInterval={GENERATION_INTERVAL}
        windowSize={6}
      />
    </>
  );
};

export default GenerateSurveyScreen;
